import { Component, Inject, OnInit } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { select, Store } from '@ngrx/store'
import {
  asLocalDate,
  asUTCEpoch,
  ConfirmDialogService,
  Destroyable,
  isSomething,
  SaveForm,
  selectAllRentals,
  selectChannelByName,
  untilDestroy,
} from '@tokeet/tv3-platform'
import { observeOn, startWith } from 'rxjs/operators'
import { asyncScheduler, BehaviorSubject, combineLatest, merge } from 'rxjs'
import * as moment from 'moment'
import { AddLosDiscount, DeleteLosDiscount } from '../../store/los-discount/los-discount.actions'
import { noAllWhitespaceValidator, omitViewFields, percent, positive } from '@tokeet-frontend/utility'
import {
  availableLosDiscountModalities,
  LosDiscount,
  LosDiscountModality,
  LosDiscountView,
} from '../../store/los-discount/los-discount.model'
import { losTokeetChannel } from '../../store/los-discount/los-discount.selectors'

export interface LostDiscountDialogParams {
  item?: Partial<LosDiscountView>
  primaryBtnClass: string
  cancelBtnClass: string
  hasModality: boolean
  articleId?: string
  videoId?: string
}

@Component({
  selector: 'app-los-discount-dialog',
  templateUrl: './los-discount-dialog.component.html',
  host: { class: 'template-overlay' },
  styleUrls: ['./los-discount-dialog.component.scss'],
})
export class LosDiscountDialogComponent extends Destroyable implements OnInit {
  form = this.fb.group({
    type: ['', [Validators.required]],
    modality: [LosDiscountModality.PerStay, [Validators.required]],
    name: ['', [Validators.required, Validators.maxLength(50), noAllWhitespaceValidator]],
    description: ['', [Validators.required, Validators.maxLength(150)]],
    amount: [undefined, [Validators.required]],
    length: [undefined, [Validators.required, Validators.min(1)]],
    guests: [undefined, [Validators.required, Validators.min(1)]],
    rentals: [[]],
    channels: [[]],
    start: [],
    end: [],
  })

  get typeCtrl() {
    return this.form.get('type')
  }

  get amountCtrl() {
    return this.form.get('amount')
  }

  rentals$ = this.store.pipe(observeOn(asyncScheduler), select(selectAllRentals))

  channels = [losTokeetChannel]
  now = new Date()

  modalities = availableLosDiscountModalities
  minEndDate = new BehaviorSubject<Date>(this.now)
  maxStartDate = new BehaviorSubject<Date>(null)

  isEdit = false

  item: Partial<LosDiscountView>

  constructor(
    private fb: FormBuilder,
    private store: Store<any>,
    private confirmDialog: ConfirmDialogService,
    @Inject(MAT_DIALOG_DATA)
    public data: LostDiscountDialogParams,
    public dialogRef: MatDialogRef<LosDiscountDialogComponent>
  ) {
    super()
  }

  ngOnInit() {
    merge(this.form.get('start').valueChanges, this.form.get('end').valueChanges)
      .pipe(startWith(null), untilDestroy(this))
      .subscribe(() => {
        this.setStartEndMinMax()
      })

    this.typeCtrl.valueChanges.pipe(untilDestroy(this)).subscribe((value) => {
      if (value === 'pct') {
        this.amountCtrl.setValidators([Validators.required, percent()])
      } else {
        this.amountCtrl.setValidators([Validators.required, positive()])
      }
      this.amountCtrl.updateValueAndValidity()
    })

    combineLatest([this.store.pipe(select(selectChannelByName, { name: 'webready' }))])
      .pipe(untilDestroy(this))
      .subscribe(([webready]) => {
        this.channels = [losTokeetChannel, webready]
      })

    this.item = this.data.item
    this.setForm(this.data.item)
  }

  setStartEndMinMax() {
    if (this.form.get('start').value) {
      const start = moment(this.form.get('start').value).add(1, 'days').startOf('day')
      this.minEndDate.next(start.toDate() < this.now ? this.now : start.toDate())
    }

    if (this.form.get('end').value) {
      const end = moment(this.form.get('end').value).subtract(1, 'days').endOf('day')
      this.maxStartDate.next(end.toDate())
    }
  }

  close() {
    this.dialogRef.close()
  }

  @SaveForm()
  onSave(form: FormGroup) {
    const formData = form.getRawValue()
    if (this.isEdit) {
    } else {
      this.store.dispatch(
        AddLosDiscount({
          payload: {
            ...formData,
            start: asUTCEpoch(formData.start),
            end: asUTCEpoch(formData.end),
          } as LosDiscount,
        })
      )
    }
    this.close()
  }

  onDelete() {
    this.confirmDialog
      .confirm({
        title: $localize`Delete discount?`,
        body: $localize`Are you sure you want to delete this discount?`,
        confirmBtnClass: this.data.primaryBtnClass,
      })
      .subscribe(() => {
        this.store.dispatch(DeleteLosDiscount({ id: this.item.id }))
        this.close()
      })
  }

  private setForm(item?: Partial<LosDiscount>) {
    if (isSomething(item)) {
      this.form.patchValue({
        ...omitViewFields(item),
        modality: item.modality || LosDiscountModality.PerStay,
        start: item.start && asLocalDate(item.start),
        end: item.end && asLocalDate(item.end),
      })
      this.isEdit = !!item.id
    }
    if (this.isEdit) {
      this.form.disable()
    }
  }
}
